/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next + TS 4.9.5 workaround — see src/i18n/index.ts.
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import styles from './ElevationModal.module.css';

const useTranslation = useTranslationUntyped as any;

interface ElevationModalProps {
    open: boolean;
    /** Called after successful verification + subsequent action (e.g., retry switch). */
    onSuccess: () => void;
    /** Called when the user cancels the modal. */
    onCancel: () => void;
}

/**
 * SuperAdmin elevation modal (AB#1437).
 *
 * Shown when the user tries to switch to a Super Admin profile and the server
 * responds with 403 Elevation (no fresh `passwordVerifiedAt` claim). On submit
 * it calls {@link useAuth.verifyPasswordV2}; on success, calls `onSuccess` so
 * the parent can retry its original action (typically `switchProfileV2`).
 *
 * 3 consecutive failed attempts close the modal with the generic lock message
 * (existing server-side lockout policy still applies).
 */
const ElevationModal: React.FC<ElevationModalProps> = ({ open, onSuccess, onCancel }) => {
    const { t } = useTranslation('auth');
    const { verifyPasswordV2, loading } = useAuth();
    const [password, setPassword] = useState('');
    const [attempts, setAttempts] = useState(0);
    const [error, setError] = useState<string | null>(null);
    // Ref holds the auto-close timer so it can be cancelled if the modal unmounts
    // before the 2-second delay fires (prevents state updates on unmounted parents).
    const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
        };
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!password || loading) return;
        setError(null);

        const ok = await verifyPasswordV2(password);
        if (ok) {
            setPassword('');
            setAttempts(0);
            onSuccess();
        } else {
            const next = attempts + 1;
            setAttempts(next);
            if (next >= 3) {
                setError(t('elevation.locked'));
                setPassword('');
                // Close after a short delay so the user can read the lock notice.
                autoCloseTimer.current = setTimeout(onCancel, 2000);
            } else {
                setError(t('elevation.incorrectPassword'));
                setPassword('');
            }
        }
    }, [password, loading, verifyPasswordV2, attempts, t, onSuccess, onCancel]);

    if (!open) return null;

    return (
        <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="elevation-title">
            <form className={styles.modal} onSubmit={handleSubmit} onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}>
                <h2 id="elevation-title" className={styles.title}>{t('elevation.title')}</h2>
                <p className={styles.body}>{t('elevation.body')}</p>
                <label className={styles.passwordLabel} htmlFor="elevation-password">
                    {t('elevation.passwordLabel')}
                </label>
                <input
                    id="elevation-password"
                    type="password"
                    className={styles.passwordInput}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoFocus
                    autoComplete="current-password"
                    disabled={loading || attempts >= 3}
                />
                {error && <div className={styles.error} role="alert">{error}</div>}
                <div className={styles.actions}>
                    <button type="button" className={styles.cancel} onClick={onCancel} disabled={loading}>
                        {t('elevation.cancel')}
                    </button>
                    <button type="submit" className={styles.continue} disabled={!password || loading || attempts >= 3}>
                        {t('elevation.continue')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ElevationModal;
